/**
 * Ball Pack Builder — League Lids (v2, 2026-07)
 *
 * Pack-size + slot-tray bundle builder for rugby league balls.
 * Mix and match any products in the bundle collection; the tiered
 * discount applies automatically in the cart.
 *
 * IMPORTANT: The discount percentages in TIERS below MUST match the
 * Shopify automatic discounts configured in admin ("Ball 2-Pack 10% Off"
 * etc., minimum-quantity on the rugby-league-balls collection). If a
 * discount changes in Shopify, update it here too, and vice versa.
 */

if (!customElements.get('ball-pack-builder')) {

  class BallPackBuilder extends HTMLElement {

    /* ── Tier config — KEEP IN SYNC WITH SHOPIFY AUTOMATIC DISCOUNTS ── */
    static TIERS = [
      { qty: 1, label: 'Single', discount: 0 },
      { qty: 2, label: '2 Pack', discount: 0.10 },
      { qty: 3, label: '3 Pack', discount: 0.15 },
      { qty: 5, label: '5 Pack', discount: 0.20 }
    ];

    static _collectionCache = {};

    connectedCallback() {
      this._readAttributes();
      this._tier = 0;               /* index into TIERS */
      this._slots = [];             /* ordered units: { handle, variantId, title, image, price } */
      this._products = [];
      this._showSoldOut = false;
      this._lastFilledIndex = -1;   /* which slot just filled (for the pop animation) */
      this._successTimeout = null;
      this._settleTimeout = null;

      this._onVariantChange = this._handleVariantChange.bind(this);
      var productInfo = this.closest('.product-info, [data-product-info], .product');
      (productInfo || document).addEventListener('variant:change', this._onVariantChange);

      this._init();
    }

    disconnectedCallback() {
      if (this._onVariantChange) {
        var productInfo = this.closest('.product-info, [data-product-info], .product');
        (productInfo || document).removeEventListener('variant:change', this._onVariantChange);
      }
      if (this._successTimeout) clearTimeout(this._successTimeout);
      if (this._settleTimeout) clearTimeout(this._settleTimeout);
      this._toggleBuyButtons(true); /* never leave native buttons hidden */
    }

    _readAttributes() {
      this._currentHandle = this.getAttribute('product-handle') || '';
      this._currentVariantId = parseInt(this.getAttribute('variant-id'), 10);
      this._currentTitle = this.getAttribute('product-title') || '';
      this._currentImage = this.getAttribute('product-image') || '';
      this._currentPrice = parseInt(this.getAttribute('product-price'), 10) || 0;
      this._currentAvailable = this.getAttribute('product-available') === 'true';
      this._collectionHandle = this.getAttribute('collection-handle') || '';
      this._moneyFormat = this.getAttribute('money-format') || '${{amount}}';
    }

    _handleVariantChange(event) {
      var variant = (event.detail || {}).variant;
      if (!variant || !variant.id) return;
      var productChanged = variant.id !== this._currentVariantId &&
                           event.detail && event.detail.productChange;
      this._currentVariantId = variant.id;
      this._currentPrice = variant.price || this._currentPrice;
      this._currentAvailable = variant.available !== false;

      /* Keep any slots holding the current product in sync */
      for (var i = 0; i < this._slots.length; i++) {
        if (this._slots[i].handle === this._currentHandle) {
          this._slots[i].variantId = this._currentVariantId;
          this._slots[i].price = this._currentPrice;
        }
      }

      if (productChanged) {
        this._slots = [];
        this._tier = 0;
        this._toggleBuyButtons(true);
      }
      this._render();
    }

    async _init() {
      try {
        if (!this._collectionHandle) {
          this.innerHTML = '';
          return;
        }
        this._products = await this._fetchCollectionProducts(this._collectionHandle);
        /* Available first, sold out last */
        this._products.sort(function (a, b) {
          var aAvail = a.variants && a.variants[0] && a.variants[0].available ? 1 : 0;
          var bAvail = b.variants && b.variants[0] && b.variants[0].available ? 1 : 0;
          return bAvail - aAvail;
        });
        if (this._products.length === 0) { this.innerHTML = ''; return; }
        this._render();
        this.setAttribute('data-loaded', '');
      } catch (e) {
        console.error('[BallPackBuilder] Init failed:', e);
      }
    }

    async _fetchCollectionProducts(collectionHandle) {
      if (BallPackBuilder._collectionCache[collectionHandle]) {
        return BallPackBuilder._collectionCache[collectionHandle];
      }
      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      var all = [];
      var page = 1;
      while (true) {
        var res = await fetch(root + 'collections/' + collectionHandle + '/products.json?limit=250&page=' + page);
        if (!res.ok) break;
        var data = await res.json();
        if (!data.products || data.products.length === 0) break;
        all = all.concat(data.products);
        if (data.products.length < 250) break;
        page++;
      }
      /* Normalise */
      all.forEach(function (p) {
        if (!p.featured_image && p.image && p.image.src) p.featured_image = p.image.src;
        if (p.images && p.images.length && typeof p.images[0] === 'object') {
          p.images = p.images.map(function (img) { return img.src; });
        }
        if (p.variants) {
          p.variants.forEach(function (v) {
            if (typeof v.price === 'string') v.price = Math.round(parseFloat(v.price) * 100);
          });
        }
      });
      BallPackBuilder._collectionCache[collectionHandle] = all;
      return all;
    }

    /* ── State helpers ── */

    _tierData() { return BallPackBuilder.TIERS[this._tier]; }

    _qtyOf(handle) {
      var n = 0;
      for (var i = 0; i < this._slots.length; i++) if (this._slots[i].handle === handle) n++;
      return n;
    }

    _isReady() {
      var t = this._tierData();
      return t.qty > 1 && this._slots.length >= t.qty;
    }

    /* Shopify allocates percentage discounts per cart line (grouped by
       variant) and floors each line's discount to the cent — mirror that
       exactly so our totals match the cart to the cent. */
    _packTotals() {
      var t = this._tierData();
      var byVariant = {};
      for (var i = 0; i < this._slots.length; i++) {
        var s = this._slots[i];
        byVariant[s.variantId] = (byVariant[s.variantId] || 0) + s.price;
      }
      var full = 0;
      var saved = 0;
      Object.keys(byVariant).forEach(function (id) {
        var line = byVariant[id];
        full += line;
        saved += Math.floor(line * t.discount);
      });
      return { full: full, saved: saved, total: full - saved };
    }

    _seedCurrent() {
      if (!this._currentAvailable) return;
      if (this._qtyOf(this._currentHandle) > 0) return;
      this._slots.unshift({
        handle: this._currentHandle,
        variantId: this._currentVariantId,
        title: this._currentTitle,
        image: this._currentImage,
        price: this._currentPrice
      });
    }

    /* ── Rendering ── */

    _render() {
      var html = '';
      html += '<div class="bpk">';

      html += '<div class="bpk__head">';
      html += '<h3 class="bpk__heading">Bundle and save</h3>';
      html += '<p class="bpk__sub">Grab a pack and save up to 20%. Mix and match any designs.</p>';
      html += '</div>';

      html += this._renderTiers();

      /* A fresh render never animates the stage — open it fully settled.
         The open/close animation only runs on live tier switches. */
      var open = this._tierData().qty > 1;
      html += '<div class="bpk__stage' + (open ? ' is-open is-settled' : '') + '">';
      html += '<div class="bpk__stage-inner">';
      html += '<div class="bpk__stage-content">';
      if (open) {
        html += this._renderTray();
        html += this._renderGrid();
        html += this._renderSummary();
      }
      html += '</div></div></div>';

      html += '</div>';
      this.innerHTML = html;
      this._bindEvents();
    }

    _renderTiers() {
      var tiers = BallPackBuilder.TIERS;
      var price = this._currentPrice;
      var html = '<div class="bpk__tiers" role="radiogroup" aria-label="Pack size">';
      for (var i = 0; i < tiers.length; i++) {
        var t = tiers[i];
        var ea = price - Math.floor(price * t.discount);
        var selected = i === this._tier;
        html += '<button type="button" class="bpk__tier' + (selected ? ' is-selected' : '') + '" role="radio" aria-checked="' + selected + '" data-tier="' + i + '">';
        if (t.discount > 0) {
          html += '<span class="bpk__tier-chip">Save ' + Math.round(t.discount * 100) + '%</span>';
        }
        html += '<span class="bpk__tier-qty">' + t.qty + '</span>';
        html += '<span class="bpk__tier-label">' + t.label + '</span>';
        html += '<span class="bpk__tier-ea">' + this._money(ea) + (t.qty > 1 ? '<small>each</small>' : '') + '</span>';
        html += '</button>';
      }
      html += '</div>';
      return html;
    }

    _renderTray() {
      var t = this._tierData();
      var html = '<div class="bpk__tray">';
      html += '<div class="bpk__slots" aria-label="Your pack">';
      for (var i = 0; i < t.qty; i++) {
        var unit = this._slots[i];
        if (unit) {
          var justFilled = i === this._lastFilledIndex;
          html += '<button type="button" class="bpk__slot is-filled' + (justFilled ? ' is-new' : '') + '" data-slot="' + i + '" aria-label="Remove ' + this._escAttr(unit.title) + '">';
          html += '<img src="' + this._escAttr(this._resizeImage(unit.image, 160)) + '" alt="" loading="lazy" width="80" height="80">';
          html += '<span class="bpk__slot-x" aria-hidden="true">&times;</span>';
          html += '</button>';
        } else {
          html += '<span class="bpk__slot" aria-hidden="true"><span class="bpk__slot-n">' + (i + 1) + '</span></span>';
        }
      }
      html += '</div>';

      var remaining = t.qty - this._slots.length;
      html += '<div class="bpk__tray-side">';
      if (remaining > 0) {
        html += '<p class="bpk__tray-status">Pick ' + remaining + ' more below</p>';
        html += '<p class="bpk__tray-hint">Tap a design to add it. Same one twice is fine.</p>';
      } else {
        html += '<p class="bpk__tray-status is-done">Pack complete</p>';
        html += '<p class="bpk__tray-hint">Tap a slot to swap something out.</p>';
      }
      if (this._slots.length > 0) {
        html += '<button type="button" class="bpk__reset" data-reset>Start over</button>';
      }
      html += '</div>';
      html += '</div>';
      return html;
    }

    _renderGrid() {
      var soldOutCount = 0;
      for (var c = 0; c < this._products.length; c++) {
        var vc = this._products[c].variants && this._products[c].variants[0];
        if (vc && !vc.available) soldOutCount++;
      }

      var html = '<div class="bpk__grid" role="listbox" aria-label="Choose your designs">';
      for (var i = 0; i < this._products.length; i++) {
        var p = this._products[i];
        var v = p.variants && p.variants[0];
        if (!v) continue;
        if (!v.available && !this._showSoldOut) continue;
        html += this._renderCard(p, v);
      }
      html += '</div>';

      if (soldOutCount > 0) {
        html += '<button type="button" class="bpk__more" data-toggle-soldout>' +
          (this._showSoldOut ? 'Hide sold out' : 'Show sold out (' + soldOutCount + ')') +
          '</button>';
      }
      return html;
    }

    _renderCard(p, v) {
      var full = this._isReady();
      var available = !!v.available;
      var qty = this._qtyOf(p.handle);
      var isCurrent = p.handle === this._currentHandle;
      var image = p.featured_image || (p.images && p.images[0]) || '';

      var cls = 'bpk__card';
      if (qty > 0) cls += ' is-selected';
      if (!available) cls += ' is-soldout';
      else if (full && qty === 0) cls += ' is-full';

      var html = '<div class="' + cls + '" role="option" aria-selected="' + (qty > 0) + '"' +
        (available ? ' tabindex="0"' : ' aria-disabled="true"') +
        ' data-handle="' + this._escAttr(p.handle) + '"' +
        ' data-variant-id="' + v.id + '"' +
        ' data-title="' + this._escAttr(p.title) + '"' +
        ' data-image="' + this._escAttr(image) + '"' +
        ' data-price="' + v.price + '">';

      html += '<span class="bpk__card-media">';
      if (image) {
        html += '<img src="' + this._escAttr(this._resizeImage(image, 360)) + '" alt="' + this._escAttr(p.title) + '" loading="lazy" width="180" height="180">';
      }
      if (isCurrent && available) html += '<span class="bpk__card-this">This one</span>';
      if (qty > 0) html += '<span class="bpk__card-qty">' + qty + '</span>';
      if (!available) html += '<span class="bpk__card-soldout">Sold out</span>';
      html += '</span>';

      html += '<span class="bpk__card-body">';
      html += '<span class="bpk__card-title">' + this._esc(this._shortTitle(p.title)) + '</span>';
      html += '<span class="bpk__card-row">';
      html += '<span class="bpk__card-price">' + this._money(v.price) + '</span>';
      if (qty > 0 && available) {
        html += '<button type="button" class="bpk__card-minus" data-minus data-handle="' + this._escAttr(p.handle) + '" aria-label="Remove one ' + this._escAttr(p.title) + '">&minus;</button>';
      }
      html += '</span>';
      html += '</span>';

      html += '</div>';
      return html;
    }

    _renderSummary() {
      var t = this._tierData();
      if (t.qty <= 1) return '';
      var ready = this._isReady();
      var remaining = t.qty - this._slots.length;

      var totals = this._packTotals();

      var html = '<div class="bpk__summary' + (ready ? ' is-ready' : '') + '">';
      if (ready) {
        html += '<div class="bpk__totals">';
        html += '<span class="bpk__was">' + this._formatMoney(totals.full) + '</span>';
        html += '<span class="bpk__now">' + this._money(totals.total) + '</span>';
        if (totals.saved > 0) html += '<span class="bpk__save">Save ' + this._formatMoney(totals.saved) + '</span>';
        html += '</div>';
      }
      html += '<button type="button" class="bpk__atc"' + (ready ? '' : ' disabled') + ' data-atc>';
      html += '<span class="bpk__atc-text">' + (ready
        ? 'Add ' + t.label + ' to cart &middot; ' + this._formatMoney(totals.total)
        : 'Pick ' + remaining + ' more to continue') + '</span>';
      html += '<span class="bpk__atc-spinner" aria-hidden="true"></span>';
      html += '</button>';
      html += '<p class="bpk__auto-note">' + Math.round(t.discount * 100) + '% comes off automatically at checkout.</p>';
      html += '</div>';
      return html;
    }

    /* ── Events ── */

    _bindEvents() {
      var self = this;

      this.querySelectorAll('.bpk__tier').forEach(function (el) {
        el.addEventListener('click', function () {
          self._selectTier(parseInt(el.dataset.tier, 10));
        });
      });

      this.querySelectorAll('.bpk__card').forEach(function (card) {
        card.addEventListener('click', function (e) {
          if (e.target.closest('[data-minus]')) return;
          self._addUnit(card);
        });
        card.addEventListener('keydown', function (e) {
          if ((e.key === 'Enter' || e.key === ' ') && !e.target.closest('[data-minus]')) {
            e.preventDefault();
            self._addUnit(card);
          }
        });
      });

      this.querySelectorAll('[data-minus]').forEach(function (btn) {
        var act = function (e) {
          e.stopPropagation();
          e.preventDefault();
          self._removeOneOf(btn.dataset.handle);
        };
        btn.addEventListener('click', act);
        btn.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') act(e);
        });
      });

      this.querySelectorAll('.bpk__slot.is-filled').forEach(function (slot) {
        slot.addEventListener('click', function () {
          self._removeSlot(parseInt(slot.dataset.slot, 10));
        });
      });

      var soldToggle = this.querySelector('[data-toggle-soldout]');
      if (soldToggle) {
        soldToggle.addEventListener('click', function () {
          self._showSoldOut = !self._showSoldOut;
          self._refreshStage();
        });
      }

      var reset = this.querySelector('[data-reset]');
      if (reset) {
        reset.addEventListener('click', function () {
          self._slots = [];
          self._seedCurrent();
          self._lastFilledIndex = self._slots.length - 1;
          self._refreshStage();
        });
      }

      var atc = this.querySelector('[data-atc]');
      if (atc) {
        atc.addEventListener('click', function () { self._addPackToCart(); });
      }
    }

    _selectTier(index) {
      if (index === this._tier) return;
      this._tier = index;
      var t = this._tierData();
      var self = this;

      /* Update the tier row in place — no re-render, no flicker */
      this.querySelectorAll('.bpk__tier').forEach(function (el) {
        var sel = parseInt(el.dataset.tier, 10) === index;
        el.classList.toggle('is-selected', sel);
        el.setAttribute('aria-checked', sel ? 'true' : 'false');
      });

      var stage = this.querySelector('.bpk__stage');
      if (this._settleTimeout) clearTimeout(this._settleTimeout);

      if (t.qty === 1) {
        this._slots = [];
        this._toggleBuyButtons(true);
        if (stage) {
          stage.classList.remove('is-open', 'is-settled');
          /* Empty the collapsed stage once the close animation finishes */
          this._settleTimeout = setTimeout(function () {
            if (self._tierData().qty !== 1) return;
            var c = self.querySelector('.bpk__stage-content');
            if (c) c.innerHTML = '';
          }, 500);
        }
        return;
      }

      if (this._slots.length === 0) this._seedCurrent();
      if (this._slots.length > t.qty) this._slots.length = t.qty;
      this._lastFilledIndex = -1;
      this._toggleBuyButtons(false);

      var wasOpen = stage && stage.classList.contains('is-open');
      this._refreshStage();

      if (stage && !wasOpen) {
        /* Opening from Single: animate open, then settle for the sticky tray */
        stage.classList.remove('is-settled');
        requestAnimationFrame(function () { stage.classList.add('is-open'); });
        this._settleTimeout = setTimeout(function () {
          if (stage.classList.contains('is-open')) stage.classList.add('is-settled');
        }, 480);

        /* Bring the tray into view if needed. Scroll the WINDOW manually —
           scrollIntoView would also scroll the overflow:hidden stage box
           mid-animation, shifting the content inside it (visible jitter). */
        var tray = this.querySelector('.bpk__tray');
        if (tray) {
          requestAnimationFrame(function () {
            var r = tray.getBoundingClientRect();
            if (r.bottom > window.innerHeight || r.top < 0) {
              var headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 64;
              window.scrollTo({ top: window.scrollY + r.top - headerH - 16, behavior: 'smooth' });
            }
          });
        }
      }
      /* Switching between open packs: the stage node is untouched — nothing
         re-animates, so there is no transient spacing. */
    }

    _addUnit(card) {
      var t = this._tierData();
      if (card.classList.contains('is-soldout') || card.classList.contains('is-full')) return;
      if (this._slots.length >= t.qty) return;
      this._slots.push({
        handle: card.dataset.handle,
        variantId: parseInt(card.dataset.variantId, 10),
        title: card.dataset.title,
        image: card.dataset.image,
        price: parseInt(card.dataset.price, 10)
      });
      this._lastFilledIndex = this._slots.length - 1;
      this._refreshStage();
    }

    _removeOneOf(handle) {
      for (var i = this._slots.length - 1; i >= 0; i--) {
        if (this._slots[i].handle === handle) {
          this._slots.splice(i, 1);
          break;
        }
      }
      this._lastFilledIndex = -1;
      this._refreshStage();
    }

    _removeSlot(index) {
      if (index < 0 || index >= this._slots.length) return;
      this._slots.splice(index, 1);
      this._lastFilledIndex = -1;
      this._refreshStage();
    }

    /* Re-render only the stage content (tray + grid + summary) */
    _refreshStage() {
      var content = this.querySelector('.bpk__stage-content');
      if (!content) return;
      content.innerHTML = this._renderTray() + this._renderGrid() + this._renderSummary();
      this._bindEvents();
    }

    _toggleBuyButtons(show) {
      var el = document.querySelector('[data-ball-buy-buttons]');
      if (el) el.style.display = show ? '' : 'none';
      var sticky = document.querySelector('.product-quick-add, [data-product-quick-add]');
      if (sticky) sticky.style.display = show ? '' : 'none';
    }

    /* ── Add to cart ── */

    async _addPackToCart() {
      var t = this._tierData();
      var btn = this.querySelector('[data-atc]');
      if (!btn || btn.disabled || !this._isReady()) return;

      /* Group slots into cart items */
      var byVariant = {};
      for (var i = 0; i < this._slots.length; i++) {
        var id = this._slots[i].variantId;
        byVariant[id] = (byVariant[id] || 0) + 1;
      }
      var items = Object.keys(byVariant).map(function (id) {
        return { id: parseInt(id, 10), quantity: byVariant[id] };
      });

      if (this._successTimeout) clearTimeout(this._successTimeout);
      btn.classList.add('is-loading');
      btn.disabled = true;

      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      try {
        /* Speak the theme's native add-to-cart protocol (mirrors Impact's
           ProductForm): bundle the cart-drawer sections into the POST so the
           drawer re-renders + opens itself off the variant:add event. */
        var sectionsToBundle = ['variant-added'];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true, detail: { sections: sectionsToBundle }
        }));

        var formData = new FormData();
        items.forEach(function (item, n) {
          formData.append('items[' + n + '][id]', item.id);
          formData.append('items[' + n + '][quantity]', item.quantity);
        });
        formData.set('sections', sectionsToBundle.join(','));
        formData.set('sections_url', root + 'variants/' + items[0].id);

        var addRes = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });

        if (!addRes.ok) {
          var errData = await addRes.json().catch(function () { return {}; });
          var errStr = JSON.stringify(errData).toLowerCase();
          if (errStr.indexOf('stock') !== -1 || errStr.indexOf('inventory') !== -1 || errStr.indexOf('available') !== -1 || errStr.indexOf('not enough') !== -1) {
            throw new Error('stock');
          }
          throw new Error('add-failed');
        }

        var addData = await addRes.json();
        var cartData = await (await fetch(root + 'cart.js')).json();
        cartData['sections'] = addData['sections'];

        this.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: { items: addData.items || [addData], cart: cartData }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart: cartData }
        }));

        btn.classList.remove('is-loading');
        btn.classList.add('is-success');
        var textEl = btn.querySelector('.bpk__atc-text');
        if (textEl) textEl.innerHTML = 'Added to cart &#10003;';

        var self = this;
        this._successTimeout = setTimeout(function () {
          self._slots = [];
          self._tier = 0;
          self._toggleBuyButtons(true);
          self._render();
        }, 2400);

      } catch (err) {
        console.error('[BallPackBuilder] Cart error:', err);
        btn.classList.remove('is-loading');
        btn.disabled = false;
        var textEl2 = btn.querySelector('.bpk__atc-text');
        if (textEl2) {
          textEl2.textContent = err.message === 'stock'
            ? 'Not enough stock. Try fewer.'
            : 'Something went wrong. Try again.';
          var self2 = this;
          setTimeout(function () { self2._refreshStage(); }, 3000);
        }
      }
    }

    /* ── Helpers ── */

    _shortTitle(t) {
      return (t || '')
        .replace(/\s*Rugby League Ball\s*/i, ' ')
        .replace(/\s*Ball Keychain\s*/i, ' Keychain ')
        .replace(/\s*-\s*(Extended|Core) Edition\s*/i, ' · $1')
        .replace(/\s+/g, ' ')
        .trim();
    }

    _formatMoney(cents) {
      var amount = (cents / 100).toFixed(2);
      return this._moneyFormat
        .replace('{{amount}}', amount)
        .replace('{{amount_no_decimals}}', String(Math.round(cents / 100)))
        .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
    }

    /* Money with the session currency code, matching the theme's native
       price rendering ("$44.99 AUD"). Shopify.currency.active reflects the
       shopper's localized currency, so this stays correct per market. */
    _money(cents) {
      var code = (window.Shopify && Shopify.currency && Shopify.currency.active) || '';
      return this._formatMoney(cents) + (code ? ' ' + code : '');
    }

    _resizeImage(src, width) {
      if (!src) return '';
      if (src.indexOf('cdn.shopify.com') !== -1 && src.indexOf('width=') !== -1) {
        return src.replace(/width=\d+/, 'width=' + parseInt(width, 10));
      }
      return src.replace(/\.(jpg|jpeg|png|gif|webp)/, '_' + width + 'x' + width + '.$1');
    }

    _esc(str) {
      var d = document.createElement('div');
      d.textContent = str || '';
      return d.innerHTML;
    }

    _escAttr(str) {
      return (str || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
  }

  customElements.define('ball-pack-builder', BallPackBuilder);
}
